import React from 'react';
import { Navigate } from 'react-router-dom';
import { useBarPackageAccess } from '@/hooks/useBarPackageAccess';
import { Loader2 } from 'lucide-react';

interface NextGenContentGateProps {
  children: React.ReactNode;
}

/**
 * Hard route gate for NextGen feature surfaces.
 *
 * Soft-paywall pattern: deep/feature pages redirect unentitled users to
 * /nextgen/pricing. Directory/listing pages and the Coach stay browseable
 * (gated in-page, not at the route level).
 *
 * Replaces the prior browse-mode-everywhere experiment (board iaIgmJnuuhVIGGgRrC4O)
 * which produced 406s and 10-second timeouts on RLS-gated tables for
 * unentitled users hitting deep routes.
 *
 * Behavior by entitlement state:
 *   none / expired   → redirect to /nextgen/pricing
 *   purchased        → redirect to /nextgen/activate (must pick cycle)
 *   trial / active / scheduled → render children
 *   unknown          → render children (fail-open; per-surface gates remain as backstop)
 */
export function NextGenContentGate({ children }: NextGenContentGateProps) {
  const { entitlementState, isLoading } = useBarPackageAccess();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-jdorange" />
      </div>
    );
  }

  switch (entitlementState) {
    case 'none':
    case 'expired':
      return <Navigate to="/nextgen/pricing" replace />;

    case 'purchased':
      return <Navigate to="/nextgen/activate" replace />;

    case 'trial':
    case 'scheduled':
    case 'active':
      return <>{children}</>;

    default:
      // Unknown state — fail-open. Per-surface gates (MCQFreeQuestionGate,
      // CoachMessageGate, LockedQuestionOverlay) remain as backstops.
      return <>{children}</>;
  }
}
